<template>
  <div class="universal-select" :class="{ 'is-loading': loading }">
    <label v-if="label" class="form-label">{{ label }}</label>
    <Multiselect
        :model-value="modelValue"
        @update:model-value="onValueChange"
        :mode="mode"
        :searchable="searchable"
        :options="options"
        :placeholder="placeholder"
        :noResultsText="noResultsText"
        :close-on-select="mode === 'single'"
        :trackBy="'name'"
        :disabled="disabled || loading"
        :loading="loading"
    >
      <template v-slot:singlelabel="{ value }">
        <div class="multiselect-single-label element">
          <img
              v-if="value.image || defaultImage"
              :src="value.image || defaultImage"
              class="image"
              alt=""
          />
          <div class="info-block">
            <span class="name">{{ value.name }}</span>
            <div v-if="value.path" class="item-path">
              <span class="item-level" :class="'level-' + value.level"></span>
              {{ value.path }}
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tag="{ option, handleTagRemove, disabled: tagDisabled }">
        <div class="multiselect-tag element" :class="{ 'is-disabled': tagDisabled }">
          <img
              v-if="option.image || defaultImage"
              :src="option.image || defaultImage"
              alt=""
          />
          <div class="info-block">
            <div class="name">{{ option.name }}</div>
          </div>
          <span
              v-if="!tagDisabled"
              class="multiselect-tag-remove"
              @mousedown.prevent="handleTagRemove(option, $event)"
          >
            <span class="multiselect-tag-remove-icon"></span>
          </span>
        </div>
      </template>

      <template v-slot:option="{ option }">
        <div class="option">
          <div v-if="option.image || defaultImage" class="image-wrap">
            <img
                class="image"
                :src="option.image || defaultImage"
                alt=""
            />
          </div>
          <div class="info-block">
            <div class="name">{{ option.name }}</div>
            <div v-if="option.path" class="item-path">
              <span class="item-level" :class="'level-' + option.level"></span>
              {{ option.path }}
            </div>
          </div>
        </div>
      </template>
    </Multiselect>
  </div>
</template>

<script setup>
import "./style.css"
import Multiselect from "@/plugins/multiselect"

defineProps({
  modelValue: {
    type: [Array, String, Number, null],
    default: null
  },
  mode: {
    type: String,
    default: "tags",
    validator: (value) => ["tags", "single"].includes(value)
  },
  searchable: {
    type: Boolean,
    default: true
  },
  options: {
    type: Array,
    required: true,
    default: () => []
  },
  defaultImage: {
    type: String,
    default: ""
  },
  label: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: "Не выбрано"
  },
  noResultsText: {
    type: String,
    default: "Результатов не найдено"
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(["update:modelValue", "onChange"])

function onValueChange(value) {
  emit("update:modelValue", value)
  emit("onChange", value)
}
</script>

<style scoped>
/* ==========================================================================
1. БАЗОВЫЕ СТИЛИ И СЛУЖЕБНЫЕ ЭЛЕМЕНТЫ МУЛЬТИСЕЛЕКТА
========================================================================== */
.universal-select :deep(.multiselect) {
  min-height: 36px !important;
  height: auto !important;
  position: relative;
}
.universal-select :deep(.multiselect-wrapper) {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  height: auto !important;
  min-height: 35px !important;
  gap: 4px;
}
.universal-select :deep(.multiselect-caret) {
  position: absolute !important;
  right: 8px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 2;
  margin: 0 !important;
}
.universal-select :deep(.multiselect-clear) {
  display: none !important;
}
.universal-select :deep(.multiselect-tags) {
  position: relative !important;
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 4px;
  margin: 0 !important;
  padding: 4px 25px 4px 4px !important;
  box-sizing: border-box !important;
}

/* СТИЛИ ПЛЕЙСХОЛДЕРА */
.universal-select :deep(.multiselect-placeholder) {
  font-size: 0.85rem !important;
  line-height: 1.2 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  padding-left: 12px !important;
  padding-right: 26px !important;
  width: 100% !important;
  max-width: calc(100% - 26px) !important;
  position: absolute !important;
  left: 0 !important;
  box-sizing: border-box;
  transition: opacity 0.15s ease;
}
.universal-select :deep(.multiselect-search:focus ~ .multiselect-placeholder),
.universal-select :deep(.is-active .multiselect-placeholder) {
  opacity: 0 !important;
  pointer-events: none;
}
.universal-select :deep(.multiselect-search) {
  padding-left: 12px !important;
  padding-right: 26px !important;
  box-sizing: border-box;
}

/* ==========================================================================
2. СТИЛИ ДЛЯ ОДИНОЧНОГО ВЫБОРА (SINGLE LABEL) С ПОДДЕРЖКОЙ КАРТИНОК
========================================================================== */
.universal-select :deep(.multiselect-single-label) {
  position: absolute !important;
  left: 0 !important;
  top: 0 !important;
  width: 100% !important;
  max-width: calc(100% - 26px) !important;
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
  padding-left: 12px !important;
  padding-right: 26px !important;
  box-sizing: border-box !important;
  background: transparent !important;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

/* Контейнер контента внутри одиночного выбора */
.universal-select :deep(.multiselect-single-label .option-row),
.universal-select :deep(.multiselect-single-label .option) {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  width: 100% !important;
  overflow: hidden !important;
}

/* Обертка картинки в одиночном выборе */
.universal-select :deep(.multiselect-single-label .option-image-wrap),
.universal-select :deep(.multiselect-single-label .image-wrap) {
  width: 16px !important;
  height: 16px !important;
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
}

/* Сама картинка (img) в одиночном выборе */
.universal-select :deep(.multiselect-single-label .option-image),
.universal-select :deep(.multiselect-single-label img) {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
  object-fit: cover !important;
  border-radius: 50% !important;
  margin-right: 5px;
}

/* Текст в одиночном выборе */
.universal-select :deep(.multiselect-single-label .option-name),
.universal-select :deep(.multiselect-single-label .name) {
  font-size: 0.85rem !important;
  line-height: 1 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

/* Скрытие лейбла при фокусе на поиск или раскрытии списка */
.universal-select :deep(.multiselect-search:focus ~ .multiselect-single-label),
.universal-select :deep(.is-active .multiselect-single-label) {
  opacity: 0 !important;
}

/* ==========================================================================
3. КОМПАКТНЫЕ СТИЛИ ДЛЯ ВЫБРАННЫХ ТЕГОВ (MULTI MODE)
========================================================================== */
.universal-select :deep(.multiselect-tag) {
  height: 24px !important;
  padding: 0 6px 0 4px !important;
  margin: 2px !important;
  max-width: 100% !important;
  display: flex;
  align-items: center;
  gap: 6px;
}
.universal-select :deep(.multiselect-tag .option-image),
.universal-select :deep(.multiselect-tag img) {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px;
  min-height: 16px;
  object-fit: cover;
  border-radius: 50%;
}
.universal-select :deep(.multiselect-tag .option-name),
.universal-select :deep(.multiselect-tag .name) {
  font-size: 0.8rem;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.universal-select :deep(.multiselect-tag-remove) {
  margin-left: 2px;
  display: inline-flex;
  align-items: center;
}

/* ==========================================================================
4. ВЫПАДАЮЩИЙ СПИСОК (OPTIONS DROPDOWN)
========================================================================== */
.universal-select :deep(.multiselect-options) {
  right: 0 !important;
  left: auto !important;
  overflow-x: hidden !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* ==========================================================================
5. СТИЛИ ЭЛЕМЕНТОВ ВНУТРИ ВЫПАДАЮЩЕГО СПИСКА
========================================================================== */
.universal-select :deep(.option-row),
.universal-select :deep(.option) {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  width: 100% !important;
  white-space: nowrap !important;
}
.universal-select :deep(.option-name),
.universal-select :deep(.name) {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  flex-grow: 1 !important;
  max-width: 280px !important;
}
.universal-select :deep(.option-image-wrap),
.universal-select :deep(.image-wrap) {
  position: relative !important;
  width: 22px !important;
  height: 22px !important;
  aspect-ratio: 1 / 1;
  flex-shrink: 0 !important;
}
.universal-select :deep(.option-image),
.universal-select :deep(.image) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: 50% !important;
}

/* ==========================================================================
6. ДОПОЛНИТЕЛЬНЫЕ ДАННЫЕ (ЗАГОЛОВКИ, ХЛЕБНЫЕ КРОШКИ, УРОВНИ)
========================================================================== */
.universal-select :deep(.option-title) {
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.5rem;
  align-items: center;
  min-height: 35px;
  display: flex;
  margin: 0 !important;
}
.universal-select :deep(.option-path),
.universal-select :deep(.item-path) {
  display: block !important;
  font-size: 11px !important;
  color: #666 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  max-width: 100% !important;
  min-height: 20px;
  padding-right: 5px;
}
.universal-select :deep(.option-level),
.universal-select .item-level {
  font-weight: bold;
}

/* Иконки уровней вложенности */
.universal-select :deep(.option-level.level-1::before),
.universal-select .item-level.level-1::before { content: "\2460"; }
.universal-select :deep(.option-level.level-2::before),
.universal-select .item-level.level-2::before { content: "\2461"; }
.universal-select :deep(.option-level.level-3::before),
.universal-select .item-level.level-3::before { content: "\2462"; }
.universal-select :deep(.option-level.level-4::before),
.universal-select .item-level.level-4::before { content: "\2463"; }
.universal-select :deep(.option-level.level-5::before),
.universal-select .item-level.level-5::before { content: "\2464"; }

.universal-select .multiselect {
  height: auto !important;
  min-height: 35px;
  position: relative;
}
</style>
